import antonLatin from "@fontsource/anton/files/anton-latin-400-normal.woff2?url";
import antonLatinExt from "@fontsource/anton/files/anton-latin-ext-400-normal.woff2?url";
import inter400 from "@fontsource/inter/files/inter-latin-400-normal.woff2?url";
import inter400Ext from "@fontsource/inter/files/inter-latin-ext-400-normal.woff2?url";
import inter600 from "@fontsource/inter/files/inter-latin-600-normal.woff2?url";
import inter600Ext from "@fontsource/inter/files/inter-latin-ext-600-normal.woff2?url";
import inter700 from "@fontsource/inter/files/inter-latin-700-normal.woff2?url";
import inter700Ext from "@fontsource/inter/files/inter-latin-ext-700-normal.woff2?url";
import { LATIN, LATIN_EXT } from "./unicode-ranges.js";

/**
 * The OPULANCIA profile's typefaces (docs/visual-references/SIGNATURE_V0_2.md): Anton 400 for
 * titles, Inter for the rest (600 secondary text, 700 logo and buttons, 400 card caption).
 * Self-hosted from @fontsource (SIL Open Font License 1.1, public/licences-polices.txt) under
 * family names of their own, so a font installed on the computer can never stand in for
 * them. Every file is loaded explicitly: the export refuses to run unless all are loaded.
 */
export const TITLE_FAMILY = "OPU Anton";
export const TEXT_FAMILY = "OPU Inter";

const FACES: Array<{ family: string; weight: string; url: string; range: string; label: string }> = [
  { family: TITLE_FAMILY, weight: "400", url: antonLatin, range: LATIN, label: "Anton 400" },
  { family: TITLE_FAMILY, weight: "400", url: antonLatinExt, range: LATIN_EXT, label: "Anton 400 (latin étendu)" },
  { family: TEXT_FAMILY, weight: "400", url: inter400, range: LATIN, label: "Inter 400" },
  { family: TEXT_FAMILY, weight: "400", url: inter400Ext, range: LATIN_EXT, label: "Inter 400 (latin étendu)" },
  { family: TEXT_FAMILY, weight: "600", url: inter600, range: LATIN, label: "Inter 600" },
  { family: TEXT_FAMILY, weight: "600", url: inter600Ext, range: LATIN_EXT, label: "Inter 600 (latin étendu)" },
  { family: TEXT_FAMILY, weight: "700", url: inter700, range: LATIN, label: "Inter 700" },
  { family: TEXT_FAMILY, weight: "700", url: inter700Ext, range: LATIN_EXT, label: "Inter 700 (latin étendu)" },
];

export interface FontStatus {
  ok: boolean;
  /** Labels of the faces that could not be loaded. */
  failed: string[];
}

let loading: Promise<FontStatus> | null = null;

/** Loads every face once; the result says which failed (nothing is substituted silently). */
export function loadFonts(): Promise<FontStatus> {
  loading ??= (async () => {
    const results = await Promise.allSettled(
      FACES.map(async (f) => {
        const face = new FontFace(f.family, `url(${f.url}) format("woff2")`, { weight: f.weight, style: "normal", unicodeRange: f.range, display: "block" });
        await face.load();
        document.fonts.add(face);
        return face;
      }),
    );
    const failed = FACES.filter((_, i) => results[i]!.status === "rejected").map((f) => f.label);
    if (failed.length) loading = null; // a later attempt may succeed (connection back)
    return { ok: failed.length === 0, failed };
  })();
  return loading;
}
